import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TEST_IDS } from '../../consts/testIds';
import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import useTranslate from '../../../../hooks/useTranslate';
import { SRT_STATES, SRT_STATES_OPTIONS } from '../../consts/states';
import DateRangePickerComponent from '../../../../components/DateRangePickerComponent';
import { useRoles } from '../../../../hooks/useRoles';
import { useOrganizationFilter } from '../../../../contexts/OrganizationFilterContext';
import useOrganizationSelector from '../../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { setSelectedOrganizationId } from '../../../../redux/slices/organizationsSlice';
import { useGetOrganismsQuery } from '../../../../services/organismsApi';
import useContracts from '../../../../components/layout/ContractSelector/hook/useContracts';
import { useGetEmpresasQuery } from '../../../../services/companyApi';
import {
    EXCLUDED_ORGANIZATION_IDS,
    EXCLUDED_CONTRACT_IDS,
    EXCLUDED_ORGANISM_IDS
} from '../../consts/excludedIds';
import { isAutoseguro } from '../../../../constants/organizationLegends';
import { ORIGEN_CARGA_ROL } from '../../../../constants/origenCargaRol';

const EXCLUDED_CUITS = ['30500052089', '30707519092'];

interface RGRLFiltersProps {
    extraFilters: any;
    handleChangeExtraFilters: (e: any) => void;
    contractsOptions: any;
    establishmentsOptions: any;
    setFilters: any;
    startDate: any;
    endDate: any;
    handleDateChange: any;
    onOrganizationChange?: () => void;
}
const RGRLFilters = (props: RGRLFiltersProps) => {
    const {
        extraFilters,
        startDate,
        endDate,
        handleDateChange,
        handleChangeExtraFilters,
        onOrganizationChange
    } = props;

    const {
        data: { data: { data: organisms } } = { data: { data: [] } },
        isFetching: isFetchingOrganisms
    } = useGetOrganismsQuery({
        idOrganizacion: extraFilters?.idOrganizacion || undefined,
        skip: 0,
        take: 999
    });

    const { data: establishmentsData, isFetching: isFetchingEstablishments } = useGetEmpresasQuery({
        skip: 0,
        take: 5000,
        idOrganismoCentral: extraFilters?.idContrato
    });

    const { contracts } = useContracts();
    const [allContracts, setAllContracts] = useState<any[]>([]);
    const [allOrganisms, setAllOrganisms] = useState<any[]>([]);
    const [allEstablishments, setAllEstablishments] = useState<any[]>([]);
    const [filteredOrganisms, setFilteredOrganisms] = useState<any[]>([]);
    const [filteredEstablishments, setFilteredEstablishments] = useState<any[]>([]);

    const { t } = useTranslation('filesGenerationSRT');
    const { translate } = useTranslate(t);

    const dispatch = useAppDispatch();

    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization, setSelectedOrganization } = useOrganizationFilter();
    const { organizations: organizationsOptions } = useOrganizationSelector();

    useEffect(() => {
        if (contracts) {
            const formattedContracts = contracts
                .filter((contract: any) => !EXCLUDED_CONTRACT_IDS.has(contract.id))
                .map((contract: any) => ({
                    id: contract.id,
                    nombre: `${contract.nombre}`
                }));
            setAllContracts(formattedContracts);
        }
    }, [contracts]);

    useEffect(() => {
        if (organisms && organisms?.length > 0) {
            const formattedOrganisms = organisms
                .filter(
                    (organismo: any) =>
                        !EXCLUDED_CUITS.includes(organismo.cuit) &&
                        !EXCLUDED_ORGANISM_IDS.has(organismo.id)
                )
                .map((organismo: any) => ({
                    label: `${organismo.cuit} - ${organismo.nombre}`,
                    value: organismo.id,
                    contrato: organismo.nroContrato,
                    idOrganismoCentral: organismo?.idOrganismoCentral
                }));
            setAllOrganisms(formattedOrganisms);
        }
    }, [organisms]);

    useEffect(() => {
        if (establishmentsData?.data?.data) {
            const formattedEstablishments = establishmentsData.data.data
                .filter(
                    (establishment: any) =>
                        !EXCLUDED_ORGANISM_IDS.has(establishment.organismo?.id) &&
                        !EXCLUDED_ORGANIZATION_IDS.has(establishment.organizacion?.id)
                )
                .map((establishment: any) => ({
                    label: `${establishment.nombre} (${t('header.extraFilters.codSRT')}: ${
                        establishment.codigoSRT
                    })`,
                    value: establishment.id,
                    organismoId: establishment.organismo?.id
                }));
            setAllEstablishments(formattedEstablishments);
        }
    }, [establishmentsData, t]);

    useEffect(() => {
        if (extraFilters?.idContrato) {
            const filtered = allOrganisms.filter(
                (organismo) => organismo.idOrganismoCentral === extraFilters.idContrato
            );
            setFilteredOrganisms(filtered);

            handleChangeExtraFilters({ target: { name: 'idOrganismo', value: null } });
        } else {
            setFilteredOrganisms(allOrganisms);
        }
    }, [extraFilters?.idContrato, allOrganisms]);

    useEffect(() => {
        if (extraFilters?.idOrganismo) {
            const filtered = allEstablishments.filter(
                (establishment) => establishment.organismoId === extraFilters.idOrganismo
            );
            setFilteredEstablishments(filtered);
        } else {
            setFilteredEstablishments(allEstablishments);
        }
    }, [extraFilters?.idOrganismo, allEstablishments]);

    useEffect(() => {
        if (selectedOrganization && extraFilters?.idOrganizacion !== selectedOrganization) {
            handleChangeExtraFilters({
                target: {
                    name: 'idOrganizacion',
                    value: selectedOrganization
                }
            });
        }

        dispatch(setSelectedOrganizationId(selectedOrganization));
    }, [selectedOrganization, extraFilters?.idOrganizacion, handleChangeExtraFilters]);

    const handleOrganizationChange = (value: any) => {
        const organizationId = value?.id ?? null;

        setSelectedOrganization(organizationId);

        handleChangeExtraFilters({
            target: {
                name: 'idOrganizacion',
                value: organizationId
            }
        });
        handleChangeExtraFilters({ target: { name: 'idContrato', value: null } });
        handleChangeExtraFilters({ target: { name: 'idOrganismo', value: null } });
        handleChangeExtraFilters({ target: { name: 'idEstablecimiento', value: null } });

        onOrganizationChange?.();
    };

    const origenCargaRolOptions = [
        { label: translate('origenCargaRolOptions.preventor'), value: ORIGEN_CARGA_ROL.PREVENTOR },
        {
            label: translate('origenCargaRolOptions.operadorSupervisor'),
            value: ORIGEN_CARGA_ROL.OPERADOR_SUPERVISOR
        }
    ];

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    {isPerfilBackOffice && (
                        <Grid item xs={3}>
                            <AutocompleteComponent
                                label={`${translate('header.extraFilters.organization')}`}
                                value={
                                    organizationsOptions.find(
                                        (org) => org.id === selectedOrganization
                                    ) || null
                                }
                                options={(organizationsOptions || []).filter(
                                    (org: any) => !isAutoseguro(org.nombre)
                                )}
                                size="small"
                                name="idOrganizacion"
                                loading={false}
                                templateLabel={(option: any) => {
                                    return `${option?.nombre}`;
                                }}
                                onChange={handleOrganizationChange}
                            />
                        </Grid>
                    )}

                    <Grid item xs={isPerfilBackOffice ? 3 : 4}>
                        <AutocompleteComponent
                            name="idContrato"
                            label={`${translate('header.extraFilters.contract')}`}
                            value={
                                allContracts.find(
                                    (option) => option.id === extraFilters?.idContrato
                                ) || null
                            }
                            options={allContracts}
                            templateLabel={(option: any) => option?.nombre || ''}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idContrato',
                                        value: value?.id ?? undefined
                                    }
                                });
                                handleChangeExtraFilters({ target: { name: 'idEstablecimiento', value: null } });
                            }}
                            testID={TEST_IDS.extraFilters.rgrl.contract}
                            clearable
                        />
                    </Grid>
                    <Grid item xs={isPerfilBackOffice ? 3 : 4}>
                        <AutocompleteComponent
                            name="organismo"
                            label={`${translate('header.extraFilters.organism')}`}
                            value={
                                filteredOrganisms.find(
                                    (option) => option.value === extraFilters?.idOrganismo
                                ) || null
                            }
                            options={filteredOrganisms}
                            templateLabel={(option: any) => option?.label || ''}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'idOrganismo', value: value?.value || '' }
                                });
                                handleChangeExtraFilters({ target: { name: 'idEstablecimiento', value: null } });
                            }}
                            disabled={isFetchingOrganisms}
                            loading={isFetchingOrganisms}
                        />
                    </Grid>
                    <Grid item xs={isPerfilBackOffice ? 3 : 4}>
                        <AutocompleteComponent
                            label={`${translate('header.extraFilters.establishment')}`}
                            size="small"
                            name="idEstablecimiento"
                            value={
                                filteredEstablishments.find(
                                    (option) => option.value === extraFilters?.idEstablecimiento
                                ) || null
                            }
                            loading={isFetchingEstablishments}
                            disabled={isFetchingEstablishments}
                            options={filteredEstablishments}
                            templateLabel={(option: any) => option?.label || ''}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'idEstablecimiento', value: value?.value || '' }
                                });
                            }}
                            testID={TEST_IDS.extraFilters.rgrl.establishment}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {/* Second row: State SRT and Date Range */}
            <Grid item xs={12}>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <DateRangePickerComponent
                            startDate={startDate}
                            endDate={endDate}
                            onChange={handleDateChange}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <AutocompleteComponent
                            label={`${translate('header.extraFilters.stateSRT')}`}
                            size="small"
                            name="idEstadoSRT"
                            disabled={false}
                            loading={false}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idEstadoSRT',
                                        value: value
                                    }
                                });
                            }}
                            options={SRT_STATES_OPTIONS}
                            templateLabel={(srtState: { id: SRT_STATES; nombre: string }) => {
                                return translate(`srtStates.${srtState?.nombre}`);
                            }}
                            value={extraFilters?.idEstadoSRT || ''}
                            testID={TEST_IDS.extraFilters.rgrl.stateSRT}
                            clearable
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <AutocompleteComponent
                            label={translate('header.extraFilters.origenCargaRol')}
                            size="small"
                            name="origenCargaRol"
                            value={
                                origenCargaRolOptions.find(
                                    (option) => option.value === extraFilters?.origenCargaRol
                                ) || null
                            }
                            options={origenCargaRolOptions}
                            templateLabel={(option: any) => option?.label || ''}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'origenCargaRol', value: value?.value }
                                });
                            }}
                            testID={TEST_IDS.extraFilters.rgrl.origenCargaRol}
                            clearable
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export { RGRLFilters };
